import { useMoney } from '@shopify/hydrogen-react';
import { shopifyConst } from "@/hooks/shopify-hooks/useShopifyConst";

/**
 * Format price and return formatted price and withoutTrailingZeros
 *
 * @param {Object} [price]
 * @param {string} [price.amount]
 * @param {string} [price.currencyCode]
 * @param {number} [quantity]
 * @returns {Object}
 */
// 格式化价格并返回格式化价格和没有尾随零
const useFormattedPrice = ({
  price = {}, quantity = 1, currencyCode = 'USD'
} = {}) => {
  if (!price?.amount) {
    const pricePlaceholder = useMoney({
      amount: '0',
      currencyCode
    });

    return {
      formattedPrice: useFormatCurrencySymbol(pricePlaceholder.localizedString, currencyCode),
      withoutTrailingZeros: useFormatCurrencySymbol(pricePlaceholder.withoutTrailingZeros, currencyCode),
    };
  }
  else {
    const money = useMoney({
      amount: price.amount * quantity,
      currencyCode: currencyCode
    });
   
    return useFormatConvertedPrice(money, currencyCode);
  }
};

/**部分手机浏览器对shopify价格转出来的币种符号不成功，会显示currencyCode本身，需要手动再次转换
 * 
 * @param {String} [price] ex: 'VND 300'
 * @param {String} [currencyCode] ex: 'USD'
 * @return {String} 
 * 
 * */
export const useFormatCurrencySymbol = (price, currencyCode) => {
  const { symbol = '', countryIsoCode = '', languageIsoCode = '',  } = shopifyConst;
  const priceStr = price.replace(/\s/g, '');
  if (priceStr.includes(currencyCode)) {
    if (symbol) {
      // 使用aem home properties 的配置
      return String(priceStr).replace(currencyCode, symbol);
    }
    if (window.Intl?.NumberFormat && languageIsoCode && countryIsoCode) {
      // 使用浏览器自带方法
      const isPrefix = String(priceStr).indexOf(currencyCode) === 0;
      const priceNum = String(priceStr).replace(currencyCode, '').replace(/\s/g, '');
      const priceSymbol = (window.Intl.NumberFormat(`${languageIsoCode}-${countryIsoCode}`, {
        style: 'currency',
        currency: currencyCode,
      }).format('0')).replace(/\s/g, '').replace('0', '');
      return isPrefix ? `${priceSymbol} ${priceNum}` : `${priceNum} ${priceSymbol}`;
    }
    return priceStr;
  }
  // 没有匹配到货币符号，则直接返回原价字符串（不要去除空格，比如加拿大法语显示千位会有空格）
  return price;
}

export const withoutTrailingZeros = (value) => {
  const str = String(value);
  if (!str.includes('.')) {
    return str;
  }
  return str
    .replace(/(\.\d*?[1-9])0+$/, '$1') // 去掉小数末尾多余的0
    .replace(/\.0+$/, '')              // 如果小数部分全是0，连同小数点一起去掉
    .replace(/\.$/, '');               // 防止出现末尾只有小数点
}

/**
 * 创建价格对象的辅助函数
 * @param {string} formattedPrice - 格式化后的价格
 * @param {string} withoutTrailingZerosPrice - 去掉尾随零的价格
 * @returns {Object} 包含格式化价格的对象
 */
const createPriceObject = (formattedPrice, withoutTrailingZerosPrice) => ({
  formattedPrice,
  withoutTrailingZeros: withoutTrailingZerosPrice
});

/**
 * 转换阿拉伯-印度数字为西方阿拉伯数字
 * @param {string} amount - 原始金额
 * @returns {string} 转换后的金额
 */
const convertArabicIndicDigits = (amount) => {
  // 移除RTL标记
  const cleanAmount = amount.replace(/‏?|\u200F/gi, '');
  
  // 阿拉伯-印度数字符号映射表
  const digitMap = {
    '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4',
    '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9',
    '٫': '.', '٬': ','
  };
  
  // 使用更高效的字符替换方法
  return cleanAmount.replace(/[٠-٩٫٬]/g, char => digitMap[char] || char);
};


export const useFormatConvertedPrice = (money, currencyCode) => {
  // 参数验证
  if (!money || !currencyCode) {
    return createPriceObject('', '');
  }

  const { countryIsoCode = '', languageIsoCode = '' } = shopifyConst;
  const { amount } = money;
  const amountNum = amount.trim();
  const htmlLang = document.documentElement.getAttribute('lang');

  // EGP货币特殊处理
  if (currencyCode === 'EGP' && countryIsoCode === 'EG') {
    // 阿拉伯语环境处理
    if (htmlLang === 'ar-EG' && languageIsoCode === 'ar') {
      const convertedAmount = convertArabicIndicDigits(amountNum);
      const cleanAmount = convertedAmount.trim();
      return createPriceObject(
        `${cleanAmount}ج.م`,
        `${withoutTrailingZeros(cleanAmount)}ج.م`
      );
    }
    
    // 英语环境处理
    if (htmlLang === 'en') {
      return createPriceObject(
        `${currencyCode}${amountNum}`,
        `${currencyCode}${withoutTrailingZeros(amountNum)}`
      );
    }
  }

  // 默认处理
  return createPriceObject(
    useFormatCurrencySymbol(money.localizedString, currencyCode),
    useFormatCurrencySymbol(money.withoutTrailingZeros, currencyCode)
  );
}

export default useFormattedPrice;